/**
 * Error reporting pipeline for Raza Pro.
 *
 * Pipeline: caller -> stringify -> noise filter -> fingerprint -> burst-buffer -> RPC -> system_error_logs
 *
 * - Caller can be: ErrorBoundary, window.onerror, unhandledrejection, console.error/warn (patched),
 *   or any code calling `reportFailure(...)` / `reportWarning(...)` directly.
 * - Errors are deduped by fingerprint (server-side UPSERT in `report_error` RPC); identical errors
 *   in a 1s burst are coalesced client-side with an `occurrence_count` increment.
 * - Each report carries: message, stack, severity, route, workspace_id, build_id, viewport,
 *   browser/userAgent, breadcrumbs, and a per-call context blob.
 */
import { supabase } from '../services/supabase';
import { getCachedEmail } from '../services/sessionCache';
import { getBreadcrumbs } from './breadcrumbs';
import { scrubSecrets, scrubObject } from './scrubSecrets';
import { isChunkMissingError } from './lazyWithRetry';

interface ErrorReportContext {
    component?: string;
    context?: string;
    userEmail?: string;
    severity?: 'error' | 'warning' | 'info';
}

/* ============================================================
 *  Stringification — never produce "[object Object]" again
 * ============================================================ */

const stringifyReason = (value: unknown): string => {
    if (value == null) return String(value);
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);

    if (value instanceof Error) {
        return value.message || value.name || 'Error';
    }

    if (typeof value === 'object') {
        const v = value as Record<string, any>;
        // Supabase / PostgREST shape
        if (typeof v.message === 'string' && v.message) {
            const code = v.code ? ` (${v.code})` : '';
            return v.message + code;
        }
        if (typeof v.error_description === 'string' && v.error_description) return v.error_description;
        if (typeof v.error === 'string' && v.error) return v.error;
        if (v.error && typeof v.error.message === 'string') return v.error.message;
        // Fetch Response shape
        if (typeof v.statusText === 'string' && typeof v.status === 'number') {
            return `${v.status} ${v.statusText}${v.url ? ' @ ' + v.url : ''}`;
        }
        // Last resort: JSON stringify (skip if empty {})
        try {
            const json = JSON.stringify(v);
            if (json && json !== '{}') return json.slice(0, 500);
        } catch { /* circular ref */ }
    }

    return Object.prototype.toString.call(value);
};

const extractStack = (value: unknown): string | undefined => {
    if (value instanceof Error) return value.stack;
    if (value && typeof value === 'object') {
        const v = value as any;
        if (typeof v.stack === 'string') return v.stack;
    }
    return undefined;
};

/* ============================================================
 *  Noise filter — patterns we don't want flooding the admin panel
 * ============================================================ */

const NOISE_PATTERNS: RegExp[] = [
    /^signal is aborted/i,                                  // AbortController on unmount/navigate
    /^The user aborted a request\b/i,
    /^AbortError\b/,
    /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)/,
    /^Non-Error promise rejection captured/,
    /^Script error\.?$/,                                    // cross-origin script with no detail
    // Safari fetch quirk on nav. \b-anchored (not ^) because app code wraps it:
    // "[getPublicProposal] Error: ...Load failed", "TypeError: Load failed" — the
    // Safari sibling of the unconditionally-filtered Chrome "Failed to fetch".
    /\bLoad failed$/,
    // Same Safari failure, JSON-stringified: PostgrestError objects log as
    // `fetchEstimates error: {"message":"TypeError: Load failed","details":"",…}`,
    // so the trailing fields defeat the $-anchor above. Match the wrapped form too.
    /"message"\s*:\s*"(TypeError: )?Load failed"/,
    /^NetworkError when attempting to fetch resource/,
    /^The operation was aborted\b/,
    // `Failed to fetch` (and its app-wrapped forms — `fetchProjects error:
    // …Failed to fetch…`, `Failed to fetch dynamically imported module: …`)
    // is thrown by the browser ONLY for network-layer failures: DNS,
    // connection refused, TLS, CORS, dead wifi. It is never thrown for an
    // HTTP error status. A real bad-URL/CORS bug fails 100% at deploy time,
    // not as a steady prod drip — so a recurring `Failed to fetch` in
    // production is always transient connectivity, not an app bug.
    // `navigator.onLine` can't gate it (reports `true` on a dead network),
    // which let one stale tab log 237 of these. Filter unconditionally —
    // this is the Chrome sibling of `Load failed` / `NetworkError` above.
    /Failed to fetch/i,
    // Mobile Safari fires this directly into console when a .js URL returns
    // HTML (stale deploy → SPA fallback). lazyWithRetry already handles the
    // companion promise rejection and force-reloads — this raw browser-side
    // log is a duplicate symptom of the same condition.
    /'text\/html' is not a valid JavaScript MIME type/i,
    // gotrue-js auto-recovery events. The library logs these to console.error
    // even though it auto-recovers (retries refresh, force-steals lock, etc.).
    // The user is unaffected — we shouldn't flood the admin panel with them.
    // Covers both wordings: "…Refresh Token Not Found" AND "…Already Used".
    /^Invalid Refresh Token/i,
    /AuthApiError.*Refresh Token/i,
    /^@supabase\/gotrue-js: Lock .* was not released within \d+ms/i,
    // detectSessionInUrl clock-skew/stale-magic-link warnings ("issued over 120s
    // ago", "expires in -14375s"). User-device condition; gotrue self-recovers.
    /Session as retrieved from URL was issued/i,
    /Session as retrieved from URL expires in/i,
    // gotrue's WebLock "steal" recovery: when a stale tab holds the auth-token
    // lock too long, a newer request force-steals it and the loser's in-flight
    // fetch aborts. Surfaces wrapped by app code (`fetchEstimates error: …`),
    // so it slips past the _refreshAccessToken stack filter. Benign — the
    // stealing request wins and the query refetches on its own.
    // Both engine wordings: Chrome's "Lock broken by another request with the
    // 'steal' option" AND Safari/Firefox's "Lock was stolen by another request"
    // (slipped through as fetchEstimates/fetchProjects errors on 06-08).
    /Lock (was )?(stolen|broken) by another request/i,
    // RLS-enforcement errors — these are the policy correctly denying access,
    // not bugs. PostgreSQL code 42501 = insufficient_privilege.
    // OJO: este patrón encaja EXACTAMENTE con lo que consolePatch produce al
    // serializar un PostgrestError, así que también se estaba comiendo los
    // fallos REALES de política (una policy mal escrita da 42501 igual que una
    // denegación correcta). Las dos frases de RLS_BUG_PATTERNS lo exceptúan.
    /"code":\s*"42501"/,
    /\bForbidden: must be (Admin|Owner|Member) of workspace\b/i,
    // PostgREST PGRST303 "JWT expired" — self-healing: gotrue's autoRefresh
    // renews the token and React Query refetches on refocus. Same class as
    // the 42501 filter above (correct denial of a stale credential). Matched
    // by the JSON code only (not a bare "JWT expired" text match) so a
    // systemic auth outage surfacing the phrase through another path would
    // still be reported.
    /"code"\s*:\s*"PGRST303"/,
    // Google Maps JS API en Safari: el script no vino de Google (proxy o
    // bloqueador de contenido) y la propia librería se queja al arrancar.
    // Condición del aparato, no de la app; el mapa ni se pinta ni se rompe.
    /NotLoadingAPIFromGoogleMapsError/,
];

// Some auth recovery events show a benign message but the diagnostic info
// lives in the stack frames (e.g. `_refreshAccessToken`). Match on stack too.
const NOISE_STACK_PATTERNS: RegExp[] = [
    /_refreshAccessToken/,                                  // scheduled JWT refresh failed; supabase-js retries internally
    /_acquireLock.*getSession/s,                            // lock contention during getSession — gotrue self-recovers
];

const EXTENSION_STACK_PATTERNS: RegExp[] = [
    /chrome-extension:\/\//,
    /moz-extension:\/\//,
    /safari-(web-)?extension:\/\//,
    // Synthetic/headless DOM environments (happy-dom, jsdom scrapers running
    // the bundle in Node). Real browser sessions on the deployed SPA never
    // produce file:// stack frames — a crawler with a plain-Chrome UA slipped
    // past BOT_UA_PATTERN this way on 2026-07-20 (Maps crash inside happy-dom).
    /\bfile:\/\/\//,
    /node_modules[\\/](happy-dom|jsdom)[\\/]/,
];

// Known crawler / preview bots. They aren't real users; their errors
// (Maps init failures, JS unsupported, etc.) pollute the admin panel.
const BOT_UA_PATTERN = /bot|crawler|spider|slurp|ahrefs|semrush|bingpreview|googlebot|facebookexternalhit|whatsapp|telegrambot|linkedinbot|discordbot|twitterbot|applebot|petalbot|yandex|baiduspider|duckduckbot/i;

const isBotUserAgent = (): boolean => {
    if (typeof navigator === 'undefined') return false;
    return BOT_UA_PATTERN.test(navigator.userAgent || '');
};

// Excepciones que ganan a TODA la lista de ruido de arriba.
//
// 1) El chunk que ya no existe (despliegue viejo / tab rancia). El mensaje real
//    es «Failed to fetch dynamically imported module: …», que el patrón
//    incondicional /Failed to fetch/i se tragaba entero — incluida la línea de
//    lazyWithRetry:88 cuyo propio comentario dice «This IS surfaced — reload
//    loops are real failure that needs attention». Se reutiliza el mismo
//    detector que decide el reintento en vez de añadir otra regex.
// 2) Un fallo genuino de política RLS. Es indistinguible de una denegación
//    correcta por el código (42501 en ambos casos), pero no por el texto: sólo
//    un INSERT/UPDATE que rompe la policy dice «new row violates…».
const RLS_BUG_PATTERNS: RegExp[] = [
    /new row violates row-level security policy/i,
    /violates row-level security policy for table/i,
];

// El log crudo de Safari móvil («Refused to execute… 'text/html' is not a valid
// JavaScript MIME type») lo emite el navegador mientras lazyWithRetry ya está
// recargando: síntoma duplicado, sigue siendo ruido. El de lazyWithRetry sí es
// el fallo real y se reconoce por su prefijo.
const RAW_MIME_NOISE = /'text\/html' is not a valid JavaScript MIME type/i;

const isChunkMissingReport = (message: string): boolean => {
    if (!isChunkMissingError(message)) return false;
    if (RAW_MIME_NOISE.test(message) && !message.includes('[lazyWithRetry]')) return false;
    return true;
};

const isNoise = (message: string, stack?: string, severity?: 'error' | 'warning' | 'info'): boolean => {
    // `reportInfo` es telemetría deliberada: quien la llama ya decidió que esa
    // línea importa. Sin esta salida no tenía sentido alguno — la lista de
    // ruido se la comía igual que a un error espontáneo. NO se extiende a
    // 'error'/'warning': consolePatch pasa severity en CADA console.error y
    // console.warn, y saltarse el filtro ahí inundaría el panel.
    if (severity === 'info') return false;
    if (isChunkMissingReport(message)) return false;
    if (RLS_BUG_PATTERNS.some(p => p.test(message))) return false;
    if (NOISE_PATTERNS.some(p => p.test(message))) return true;
    if (stack && EXTENSION_STACK_PATTERNS.some(p => p.test(stack))) return true;
    if (stack && NOISE_STACK_PATTERNS.some(p => p.test(stack))) return true;
    // Safari, DENTRO de la librería de Google Maps (`maps-api-v3/…/main.js`):
    // observa un nodo que ya no es un Element. Sólo con esa pila — el mismo
    // mensaje desde nuestro código sí sería nuestro.
    if (/IntersectionObserver\.observe/.test(message) && stack && /maps\.googleapis\.com/.test(stack)) return true;
    // Offline fetch failures aren't bugs in our code
    if (/Failed to fetch|NetworkError/i.test(message) && typeof navigator !== 'undefined' && !navigator.onLine) return true;
    return false;
};

// Dev sessions running locally (npm run dev) shouldn't report errors to the
// production admin panel. They're often mid-HMR, pre-syntax-error states.
const isLocalDevSession = (): boolean => {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]') return true;
    if (host.endsWith('.local') || host.endsWith('.localhost')) return true;
    // Vite's BUILD_ID stub when the bundle wasn't statically rebuilt.
    if (BUILD_ID === 'dev') return true;
    return false;
};

/* ============================================================
 *  Fingerprint — group identical errors so the admin panel sees
 *  one row per unique bug, not one per occurrence
 * ============================================================ */

const normalizeForFingerprint = (s: string): string => {
    return s
        .replace(/https?:\/\/[^\s)'"]+/g, 'URL')
        .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, 'UUID')
        .replace(/:\d+:\d+/g, ':L:C')
        .replace(/\b\d{4,}\b/g, 'N');
};

const firstStackFrame = (stack?: string): string => {
    if (!stack) return '';
    const lines = stack.split('\n').map(l => l.trim()).filter(Boolean);
    // Skip the message line; first frame usually starts with "at " (Chrome) or includes "@" (Safari/FF)
    const frame = lines.find(l => /^at\s|@/.test(l)) ?? lines[0] ?? '';
    return frame;
};

const djb2Hex = (str: string): string => {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
};

// Componentes cuyo primer frame NO discrimina nada: consolePatch envuelve el
// mensaje en un `new Error(...)` creado dentro de logger.ts, así que el frame es
// siempre el nombre minificado de esta misma función — que cambia en cada
// despliegue — y encima Chrome lo escribe `at x (URL)` y Safari `x@URL`. El
// resultado era un bug partido en una fila por build Y por navegador. Para
// ErrorBoundary / onerror el frame sí dice dónde pasó: ahí se conserva.
const FRAMELESS_FINGERPRINT_COMPONENTS = new Set(['console.error', 'console.warn']);

const fingerprintFor = (message: string, stack: string | undefined, component: string | undefined): string => {
    const normMsg = normalizeForFingerprint(message);
    const normFrame = component && FRAMELESS_FINGERPRINT_COMPONENTS.has(component)
        ? ''
        : normalizeForFingerprint(firstStackFrame(stack));
    return djb2Hex(`${component ?? ''}|${normMsg}|${normFrame}`);
};

/* ============================================================
 *  Per-call context — route, workspace, build, viewport
 * ============================================================ */

const safeLocalStorage = (key: string): string | null => {
    try { return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null; }
    catch { return null; }
};

/** Ruta SIN query string, a propósito. `/proposal/<id>?t=<token>` es un enlace
 *  público firmado: el token es la credencial. Se estaban guardando en claro en
 *  system_error_logs (cinco todavía vivos el 2026-08-22). Lo que quedaba del
 *  query nunca sirvió para depurar nada; el `?t=` sí abría propuestas ajenas. */
const getRoute = (): string | null => {
    if (typeof window === 'undefined') return null;
    return window.location.pathname || '/';
};

const getWorkspaceId = (): string | null => safeLocalStorage('2bros_active_workspace');

const getViewport = (): Record<string, number> | null => {
    if (typeof window === 'undefined') return null;
    return {
        w: window.innerWidth,
        h: window.innerHeight,
        dpr: window.devicePixelRatio || 1,
    };
};

const BUILD_ID = (() => {
    try { return typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev'; }
    catch { return 'dev'; }
})();

/* ============================================================
 *  Burst-buffer — coalesce identical errors fired within 1s
 *  into one RPC call with occurrence_count = N
 * ============================================================ */

interface PendingReport {
    payload: RpcPayload;
    count: number;
    timer: ReturnType<typeof setTimeout> | null;
}

interface RpcPayload {
    p_message: string;
    p_stack: string | null;
    p_user_email: string | null;
    p_severity: 'error' | 'warning' | 'info';
    p_fingerprint: string;
    p_context: Record<string, unknown>;
    p_route: string | null;
    p_workspace_id: string | null;
    p_build_id: string;
    p_viewport: Record<string, number> | null;
    p_breadcrumbs: unknown[] | null;
}

const FLUSH_DELAY_MS = 1000;
const MAX_BUFFER_ENTRIES = 50;
const pendingByFingerprint = new Map<string, PendingReport>();

// Per-fingerprint rate limiter. A single stale-build user previously generated
// 235 occurrences of "Failed to fetch" in one window — that's one bug, but the
// 1-second flush coalesced only the bursts, not the slow drip. After this
// many flushes for one fingerprint inside RATE_WINDOW_MS, subsequent reports
// are silently incremented onto a counter and emitted as a single rollup
// flush when the cooldown expires.
const RATE_WINDOW_MS = 60_000;          // sliding window
const RATE_LIMIT_FLUSHES = 5;           // allow N flushes per window per fp
// 60s, no 5 min: el rollup de una tanda sólo llega al servidor cuando expira el
// enfriamiento, y con cinco minutos casi siempre moría con la pestaña. flushAll
// ahora también lo drena al cerrar, pero acortar la ventana es lo que hace que
// el panel vea la tanda mientras todavía está pasando.
const COOLDOWN_AFTER_LIMIT_MS = 60_000;
interface RateState {
    flushesInWindow: number[];   // timestamps of recent flushes
    cooldownUntil: number;       // 0 if not cooling down
    suppressedCount: number;     // occurrences accumulated during cooldown
    lastPayload: RpcPayload | null;
    cooldownTimer: ReturnType<typeof setTimeout> | null;
}
const rateByFingerprint = new Map<string, RateState>();

// Re-entrancy guard — prevents infinite loops if the RPC call itself logs an error.
//
// Era un booleano puesto a `true` durante TODO el await del RPC, y reportFailure
// se rendía nada más verlo: cualquier informe levantado en esa ventana se perdía
// entero. Eso —no el limitador de frecuencia— es lo que podía tragarse el PRIMER
// informe de un bug nuevo durante una tanda. Ahora se cuenta la profundidad, y el
// informe re-entrante se aparca en `pendingByFingerprint` en vez de tirarse.
let loggerDepth = 0;

// Tope duro: si el propio RPC encadena informes, a partir de aquí sí se tiran —
// pero se cuentan, y la cuenta viaja en el siguiente informe que sí salga, para
// que la pérdida se vea en el panel en vez de ser invisible.
//
// OJO con lo que mide de verdad: `loggerDepth` sube alrededor del `await` del
// RPC, así que cuenta vuelos SIMULTÁNEOS, no recursión — `flushOne` nunca se
// anida, lo disparan temporizadores independientes, uno por huella. Con el tope
// en 3, tres huellas distintas levantadas en el mismo segundo (justo la tanda
// que este guardia existe para sobrevivir) volvían a tragarse todo lo demás.
// El agujero negro que se vino a cerrar, con otro número delante.
//
// El tope se sube a un valor que sólo alcanza una recursión de verdad. La
// recursión ya está cortada aguas arriba por dos cosas: el informe re-entrante
// se aparca en `pendingByFingerprint` en lugar de tirarse, y `consolePatch`
// suprime cualquier mensaje que empiece por `[logger]`.
const MAX_LOGGER_DEPTH = 25;
let droppedWhileFlushing = 0;

// `keepalive: true` para el vaciado final. El navegador deja terminar esas
// peticiones aunque el documento ya se haya ido. NO se usa `sendBeacon`: no
// admite cabeceras y PostgREST exige `apikey`, o sea que el beacon saldría 401.
// Se envuelve `fetch` una sola vez, en el primer cierre, y el envoltorio sólo
// cambia algo mientras `unloading` esté puesto (bfcache lo devuelve a false:
// keepalive limita el cuerpo a 64 KB y no puede quedarse puesto para siempre).
let unloading = false;
let keepaliveInstalled = false;
let unloadingReset: ReturnType<typeof setTimeout> | null = null;

// `beforeunload` TAMBIÉN se dispara cuando el usuario CANCELA la salida, y esta
// app arma ese diálogo a propósito en dos sitios cuyo objeto es que se cancele
// (la subida TUS de una clase, los ajustes sin guardar). Si `unloading` se
// quedara puesto, cada `fetch` de la pestaña saldría con `keepalive: true` el
// resto de la sesión — y keepalive limita el CUERPO a 64 KB, o sea que se
// romperían las subidas de fotos y cualquier escritura JSON grande.
//
// Así que el aviso caduca: 3 s de margen para que la petición final salga, y si
// la pestaña sigue viva se desarma sola. Volver a la pestaña también lo desarma.
const armarSalida = (): void => {
    unloading = true;
    if (unloadingReset) clearTimeout(unloadingReset);
    unloadingReset = setTimeout(() => { unloading = false; unloadingReset = null; }, 3000);
};

const desarmarSalida = (): void => {
    unloading = false;
    if (unloadingReset) { clearTimeout(unloadingReset); unloadingReset = null; }
};

const installKeepaliveFetch = (): void => {
    if (keepaliveInstalled) return;
    const g = globalThis as any;
    const original = g.fetch;
    if (typeof original !== 'function') return;
    keepaliveInstalled = true;
    g.fetch = (input: any, init?: any) =>
        unloading ? original(input, { ...(init || {}), keepalive: true }) : original(input, init);
};

const flushOne = async (fingerprint: string) => {
    const entry = pendingByFingerprint.get(fingerprint);
    if (!entry) return;
    pendingByFingerprint.delete(fingerprint);
    if (entry.timer) clearTimeout(entry.timer);

    // Update rate state for this fingerprint
    recordFlush(fingerprint);

    loggerDepth += 1;   // ← ver nota en MAX_LOGGER_DEPTH: esto cuenta vuelos SIMULTÁNEOS
    try {
        // Scrub secret-shaped strings (API keys, JWTs, bearer tokens) from every
        // text field before the row leaves the browser. This is the single biggest
        // defence against the Gemini-key-leak pattern that landed in system_error_logs
        // 7 times. Pair with each Edge Function's server-side scrub in Phase 2.
        //
        // `p_route` se quedaba fuera de este barrido, y por ahí entraron los cinco
        // tokens de propuesta pública que aparecieron en claro. getRoute() ya no
        // manda el query string, pero se scrubea igual: esta es la última línea
        // antes de que la fila salga del navegador. El `null` se preserva.
        const { error } = await supabase.rpc('report_error', {
            ...entry.payload,
            p_message:     scrubSecrets(entry.payload.p_message),
            p_stack:       scrubSecrets(entry.payload.p_stack),
            p_route:       entry.payload.p_route ? scrubSecrets(entry.payload.p_route) : null,
            p_context:     scrubObject(entry.payload.p_context),
            p_breadcrumbs: scrubObject(entry.payload.p_breadcrumbs),
            p_increment:   entry.count,
        });
        if (error) {
            // Use the original console (consolePatch.ts saves a reference) — never call patched console.error here
            (console as any).__originalError?.('[logger] RPC failed:', error.message ?? error) ?? console.warn('[logger] RPC failed:', error);
        }
    } catch (e) {
        (console as any).__originalError?.('[logger] flush threw:', e) ?? console.warn('[logger] flush threw:', e);
    } finally {
        loggerDepth = Math.max(0, loggerDepth - 1);
    }
};

/** Update the per-fingerprint rate window after a successful flush. If the
 *  fingerprint exceeds the limit, mark it as cooling down — subsequent
 *  reports will be accumulated and emitted as a single rollup. */
const recordFlush = (fingerprint: string): void => {
    const now = Date.now();
    let state = rateByFingerprint.get(fingerprint);
    if (!state) {
        state = { flushesInWindow: [], cooldownUntil: 0, suppressedCount: 0, lastPayload: null, cooldownTimer: null };
        rateByFingerprint.set(fingerprint, state);
    }
    // Prune outside the sliding window
    state.flushesInWindow = state.flushesInWindow.filter(t => now - t < RATE_WINDOW_MS);
    state.flushesInWindow.push(now);
    if (state.flushesInWindow.length >= RATE_LIMIT_FLUSHES && state.cooldownUntil < now) {
        state.cooldownUntil = now + COOLDOWN_AFTER_LIMIT_MS;
    }
};

/** Returns true if this fingerprint is currently rate-limited. Records the
 *  suppressed occurrence (and most-recent payload) so we can roll it up
 *  when the cooldown expires. */
const isRateLimited = (fingerprint: string, payload: RpcPayload): boolean => {
    const state = rateByFingerprint.get(fingerprint);
    if (!state) return false;
    const now = Date.now();
    if (state.cooldownUntil <= now) return false;
    state.suppressedCount += 1;
    state.lastPayload = payload;
    // Schedule a single rollup flush at cooldown expiry (idempotent — only
    // sets the timer once per cooldown cycle)
    if (!state.cooldownTimer) {
        const delay = Math.max(0, state.cooldownUntil - now);
        state.cooldownTimer = setTimeout(() => {
            const s = rateByFingerprint.get(fingerprint);
            if (!s) return;
            const count = s.suppressedCount;
            const lastPayload = s.lastPayload;
            s.suppressedCount = 0;
            s.lastPayload = null;
            s.cooldownTimer = null;
            // Reset the rate window so the next genuine flush is allowed
            s.flushesInWindow = [];
            s.cooldownUntil = 0;
            if (count > 0 && lastPayload) {
                // Re-enqueue as a single rollup — the normal flush path will
                // call recordFlush again, but the window is empty so it
                // won't immediately re-enter cooldown.
                const rollup: PendingReport = {
                    payload: {
                        ...lastPayload,
                        p_context: { ...(lastPayload.p_context || {}), _rolled_up_during_cooldown: count },
                    },
                    count,
                    timer: null,
                };
                pendingByFingerprint.set(fingerprint, rollup);
                rollup.timer = setTimeout(() => flushOne(fingerprint), 0);
            }
        }, delay);
    }
    return true;
};

/** Convierte en informes pendientes los rollups acumulados en el enfriamiento.
 *  Viven sólo en `rateByFingerprint` y su único camino al servidor era el
 *  temporizador de expiración: al cerrar la pestaña se perdían enteros, que es
 *  justo cuando más ocurrencias llevan acumuladas. */
const drainCooldownRollups = (): void => {
    for (const [fp, state] of Array.from(rateByFingerprint.entries())) {
        if (state.suppressedCount <= 0 || !state.lastPayload) continue;
        const count = state.suppressedCount;
        const lastPayload = state.lastPayload;
        state.suppressedCount = 0;
        state.lastPayload = null;
        if (state.cooldownTimer) {
            clearTimeout(state.cooldownTimer);
            state.cooldownTimer = null;
        }
        const existing = pendingByFingerprint.get(fp);
        if (existing) {
            existing.count += count;
            continue;
        }
        pendingByFingerprint.set(fp, {
            payload: {
                ...lastPayload,
                p_context: { ...(lastPayload.p_context || {}), _rolled_up_during_cooldown: count },
            },
            count,
            timer: null,
        });
    }
};

const flushAll = () => {
    drainCooldownRollups();
    installKeepaliveFetch();
    armarSalida();
    for (const fp of Array.from(pendingByFingerprint.keys())) {
        // fire-and-forget on unload — the page is going away
        flushOne(fp);
    }
};

if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', flushAll);
    window.addEventListener('pagehide', flushAll);
    // La pestaña vuelve del bfcache: ya no estamos cerrando.
    window.addEventListener('pageshow', desarmarSalida);
    // Y si la pestaña vuelve a estar a la vista, la salida se canceló.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') desarmarSalida();
    });
}

const enqueue = (payload: RpcPayload) => {
    const fp = payload.p_fingerprint;

    // Hot fingerprint? Suppress and let the cooldown rollup emit a single row.
    if (isRateLimited(fp, payload)) return;

    const existing = pendingByFingerprint.get(fp);
    if (existing) {
        existing.count += 1;
        existing.payload = payload; // keep most recent sample for context/breadcrumbs/viewport
        return;
    }

    // Cap buffer size — drop oldest if we somehow accumulate too many distinct fingerprints
    if (pendingByFingerprint.size >= MAX_BUFFER_ENTRIES) {
        const oldest = pendingByFingerprint.keys().next().value;
        // Nunca de forma síncrona dentro de un flush en vuelo: sería exactamente
        // la recursión que el contador de profundidad está evitando.
        if (oldest) {
            if (loggerDepth > 0) setTimeout(() => flushOne(oldest), 0);
            else flushOne(oldest);
        }
    }

    const entry: PendingReport = { payload, count: 1, timer: null };
    pendingByFingerprint.set(fp, entry);
    entry.timer = setTimeout(() => flushOne(fp), FLUSH_DELAY_MS);
};

/* ============================================================
 *  Public API
 * ============================================================ */

export const reportFailure = async (error: unknown, info?: ErrorReportContext) => {
    // Sólo se tira al pasar el tope de re-entrancia, y queda constancia. Por
    // debajo del tope el informe sigue su camino: `enqueue` lo aparca y lo manda
    // el temporizador, ya fuera del flush que estaba en vuelo.
    if (loggerDepth >= MAX_LOGGER_DEPTH) {
        droppedWhileFlushing += 1;
        return;
    }
    if (isBotUserAgent()) return; // crawler hitting public pages — not a user bug
    if (isLocalDevSession()) return; // localhost dev — HMR transients aren't prod bugs
    // `UserInputError` es el centinela de services/storage.ts para «el archivo que
    // eligió el usuario no vale»: una validación que hizo su trabajo, no un fallo
    // del que enterarse. Se corta aquí, una vez, en vez de en cada catch de subida
    // — donde estaba puesto en 4 de los N sitios que lo necesitan.
    if ((error as Error | null)?.name === 'UserInputError') return;

    try {
        const message = stringifyReason(error);
        const stack = extractStack(error);
        const severity = info?.severity ?? 'error';

        if (isNoise(message, stack, info?.severity)) return;

        // Resolve user email (caller-supplied, else cached session, else null).
        // We read from the module-level session cache instead of calling
        // supabase.auth.getSession() — the lock contention from doing the
        // latter per error report was the root cause of the AbortError
        // cascade this logger was reporting in the first place.
        const userEmail = info?.userEmail ?? getCachedEmail();

        const browserInfo = typeof window !== 'undefined'
            ? `${navigator.userAgent}\nURL: ${window.location.href}`
            : '';

        const context: Record<string, unknown> = {};
        if (info?.component) context.component = info.component;
        if (info?.context) context.detail = info.context;
        if (browserInfo) context.browser = browserInfo;
        // Si se perdió algo por re-entrancia, que se vea: viaja con el siguiente
        // informe que sí sale, y se pone a cero al hacerlo.
        if (droppedWhileFlushing > 0) {
            context._droppedWhileFlushing = droppedWhileFlushing;
            droppedWhileFlushing = 0;
        }

        const fingerprint = fingerprintFor(message, stack, info?.component);
        const breadcrumbs = getBreadcrumbs();

        const payload: RpcPayload = {
            p_message: message.slice(0, 2000),
            p_stack: stack ? stack.slice(0, 8000) : null,
            p_user_email: userEmail,
            p_severity: severity,
            p_fingerprint: fingerprint,
            p_context: context,
            p_route: getRoute(),
            p_workspace_id: getWorkspaceId(),
            p_build_id: BUILD_ID,
            p_viewport: getViewport(),
            p_breadcrumbs: breadcrumbs.length ? breadcrumbs : null,
        };

        enqueue(payload);
    } catch (e) {
        (console as any).__originalError?.('[logger] failed to enqueue:', e) ?? console.warn('[logger] failed to enqueue:', e);
    }
};

export const reportWarning = (message: string, info?: Omit<ErrorReportContext, 'severity'>) => {
    return reportFailure(new Error(message), { ...info, severity: 'warning' });
};

export const reportInfo = (message: string, info?: Omit<ErrorReportContext, 'severity'>) => {
    return reportFailure(new Error(message), { ...info, severity: 'info' });
};

/** Test helper — exported for unit tests, not used at runtime. */
export const __internals = { fingerprintFor, isNoise, stringifyReason, estaSaliendo: () => unloading };
